import { spacing, white, BreakpointWidths, red, Flex } from '@pelotoncycle/design-system';
import type { FC } from 'react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Links } from '@peloton/links';
import { Copy, useLocale } from '../../copy';
import { SupportedLocale } from '../../copy/models';
import { useHref } from '../../hooks/useHref';
import { useAuth } from '../auth/OauthProvider';
import { footerSubLink } from '../sharedLinkStyles';
import { FontWeight } from '../ui/presets';

const getCopyrightIdForFooter = (locale: SupportedLocale) => {
  switch (locale) {
    case SupportedLocale.GermanGermany:
      return 'site.copyrightDE';
    case SupportedLocale.EnglishUnitedKingdom:
      return 'site.copyrightGB';
    case SupportedLocale.EnglishUnitedStates:
    default:
      return 'site.copyright';
  }
};

const FooterLink: FC<React.PropsWithChildren<{ href: string; fontSize?: number }>> = ({
  href,
  children,
}) => {
  const [hover, setHover] = useState(false);

  return (
    <StyledButton
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => setHover(false)}
    >
      <Link href={href}>
        <div className={`${hover ? 'hovered' : ' '}`}>{children}</div>
      </Link>
    </StyledButton>
  );
};

const UpdatedFooter: React.FC<React.PropsWithChildren> = () => {
  const links = {
    tosHref: Links.tos,
    privacyHref: Links.privacy,
    membershipTermsHref: Links.membershipTerms,
  };

  const { authEnv } = useAuth();
  const locale = useLocale();
  const copyrightId = getCopyrightIdForFooter(locale);

  const hrefs = useHref(authEnv, links);

  return (
    <Container>
      <SubLinks>
        <FooterLink href={hrefs.tosHref}>
          <Copy id="links.termsOfService" />
        </FooterLink>
        <FooterLink href={hrefs.privacyHref}>
          <Copy id="links.privacyPolicy" />
        </FooterLink>
        <FooterLink href={hrefs.membershipTermsHref}>
          <Copy id="links.membershipTerms" />
        </FooterLink>
      </SubLinks>
      <Copyright>
        <Flex flexDirection={{ mobile: 'column', desktop: 'row' }} textAlign="center">
          <span>
            <Copy
              id={copyrightId}
              args={{ startYear: '2018', endYear: new Date().getFullYear() }}
            />
            {'. '}
          </span>
          <span>
            <Copy id="site.allRightsReserved" />
          </span>
        </Flex>
      </Copyright>
    </Container>
  );
};

const Container = styled.footer`
  align-items: center;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin: ${spacing[40]} 0 32px;
  width: 100%;
`;

const SubLinks = styled.ul`
  ${footerSubLink};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  @media (min-width: ${BreakpointWidths.tablet}px) {
    flex-direction: row;
    margin-top: 60px;
  }
`;

const Link: React.FC<React.PropsWithChildren<LinkProps>> = props => (
  <LinkWrapper>
    <StyledLink href={props.href} target="_blank" rel="noopener">
      {props.children}
    </StyledLink>
  </LinkWrapper>
);

const StyledButton = styled.button`
  font-weight: ${FontWeight.semibold};
  @media (min-width: ${BreakpointWidths.tablet}px) {
    font-weight: ${FontWeight.medium};
    &:not(:last-child) {
      border-right: 1px solid ${white};
      padding-right: 6px;
    }
  }
  .hovered {
    transition: 0.25s;
    color: ${red[50]};
  }
`;

const StyledLink = styled.a`
  color: ${white};
`;

const LinkWrapper = styled.li`
  display: inline-block;
`;

const Copyright = styled.div`
  ${footerSubLink};
  font-weight: ${FontWeight.light};
  color: ${white};
  padding-top: ${spacing[24]};
  @media (min-width: ${BreakpointWidths.tablet}px) {
    padding-top: 10px;
  }
`;

type LinkProps = {
  href: string;
  fontSize?: number;
};

export default UpdatedFooter;
